import type {
    ComposableOptionsBase,
    FullImage,
    FullImageStandardSizing,
    FullImageGeneratedSizes,
    FullImageSizesList
} from '~/types';
import {
    useCdnResource
} from 'x1-base/composables';
import {
    fallbackImgUrlBlank_1x1
} from 'x1-base/constants';
import { random as lo_random } from 'es-toolkit';
import {
    imageSizeTypes,
    imageSizesDef,
    type ImageSizeTypes
} from '~/models';

const cdnRepo = 'cms-static';
const fallbackImgPath = '/products/img/fallback-400.jpg';

export function isFallbackImageUrl(url: Nilish<string>): boolean {
    return !!url && url.endsWith(fallbackImgPath);
}


export interface UseFullImageOptions extends ComposableOptionsBase {}
export function useFullImage(options: Partial<UseFullImageOptions> = {}) {
    const { getResourceCdnUrl } = useCdnResource();
    const _fallbackImage = getResourceCdnUrl(fallbackImgPath, cdnRepo);
    const fallbackImage = ref<string>(_fallbackImage);
    const genFallbackUid = () => `full_image_temp_${Math.round(lo_random(-3000, -1))}`;
    const imageSizeTypesOrdered = imageSizeTypes.slice().reverse();


    function getFullImgFromUrl(url: string, alt: string = ''): FullImage {
        return {
            url,
            alt,
            uid: genFallbackUid(),
            showDropShadow: false,
            clippedEdges: []
        };
    }

    function getFallbackImage(obj: Partial<FullImage> = {} ): FullImage {
        return Object.assign<
            FullImage,
            Partial<FullImage>
        >({
            uid: genFallbackUid(),
            url: fallbackImage.value,
            alt: 'Image not found',
            showDropShadow: false,
            clippedEdges: []
        }, obj);
    }

    function getSingleImageUrlFromFullImage(fullImage: Nilish<FullImage>, imgType: ImageSizeTypes): Nullable<string> {
        if (fullImage) {
            try {
                const sizeDef = imageSizesDef[imgType];
                const { url } = fullImage;
                const { w, h, q } = sizeDef;
                const sizeDefPart = makeImageParamPath(w, h, q);
                return url ? url.replace('/default/', `/${sizeDefPart}/`) : fallbackImgUrlBlank_1x1;
            } catch (err) {}
        }

        return null;
    }

    function getImgAttrsFromFullImage(fullImage: FullImage, overrideSizesList?: Nilish<ImageSizeTypes[]>): FullImageGeneratedSizes {
        const srcset: string[] = [];
        const sizes: string[] = [];
        const widths: number[] = [];
        const mqSizes: string[] = [];
        const sizesMap: Record<string, string> = {};
        const sizesList: FullImageSizesList[] = [];
        const { url } = fullImage;
        const overrideSizesListOrdered = overrideSizesList?.slice().reverse();

        (overrideSizesListOrdered ?? imageSizeTypesOrdered).forEach((imgType: ImageSizeTypes, index) => {
            const sizeDef = imageSizesDef[imgType];
            const { w: width, h: height, q: quality } = sizeDef;
            const sizeDefPart = makeImageParamPath(width, height, quality);
            const finalUrl = url ? url.replace('/default/', `/${sizeDefPart}/`) : fallbackImgUrlBlank_1x1;
            const valMqSizes = `(min-width: ${width}px)`;

            widths.push(width);
            mqSizes.push(valMqSizes);
            srcset.push(`${finalUrl} ${width}w`);
            sizes.push(`${valMqSizes} ${width}px`);
            sizesMap[imgType] = finalUrl;
            sizesList.push([width, height, imgType]);
        });

        return {
            sizesMap,
            sizesList,
            widths,
            mqSizes,
            srcset: srcset.join(','),
            sizes: sizes.join(',')
        };
    }

    function makeImageParamPath(w: number, h: number, q: number, c?: string): string {
        let path = `w${w}-h${h}-q${q}`;

        if (c) {
            path = `${path}-${c}`;
        }

        return path;
    }

    return {
        getFullImgFromUrl,
        fallbackImage,
        getFallbackImage,
        getSingleImageUrlFromFullImage,
        getImgAttrsFromFullImage
    };
}
